import type { EnvironmentId, ProjectId, ThreadId } from "@cmmd-forge/contracts";

import type { NoActiveThreadCommandTaskRow } from "../NoActiveThreadState.logic";
import type { SidebarThreadSummary } from "~/types";
import {
  type AgentStatus,
  buildLinkedThreadByTaskId,
  deriveAgentProvider,
  deriveAgentStatus,
  resolveActivityTimestamp,
  resolveLinkedTaskThread,
} from "~/lib/taskThreadLink";

// ---------------------------------------------------------------------------
// Unified work-item model — one type that normalises CMMD tasks and local
// threads so the dashboard can render a single sortable/filterable list.
// ---------------------------------------------------------------------------

export type WorkItemSource = "cmmd-task" | "cmmd-campaign" | "cmmd-project" | "local-thread";

// Re-exported for existing consumers; the implementation now lives in
// ~/lib/taskThreadLink so the CMMD project task surface can share it.
export type { AgentStatus };

export interface WorkItem {
  readonly id: string;
  readonly title: string;
  readonly source: WorkItemSource;

  readonly status: string | null;
  readonly priority: string | null;
  readonly projectName: string;
  readonly cmmdProjectId?: string | null;
  readonly projectIcon?: string | null;
  readonly projectIconUrl?: string | null;
  readonly campaignName: string | null;
  readonly workspaceId: string | null;
  readonly workspaceName: string | null;
  readonly workspaceIconUrl?: string | null;
  readonly teamId: string | null;
  readonly teamName: string | null;
  readonly assigneeId: string | null;
  readonly dueDate: string | null;

  readonly agentStatus: AgentStatus;
  readonly agentProvider: string | null;

  readonly environmentId: EnvironmentId | null;
  readonly threadId: ThreadId | null;
  readonly projectId: ProjectId | null;

  readonly activityAt: string;
  readonly hasActionableProposedPlan: boolean;
  readonly needsAttention: boolean;

  /**
   * WorkItem ids of tasks this item is blocked by. Already in WorkItem id
   * form (`cmmd:<taskId>`), not raw CMMD ids, so consumers can resolve
   * them against the same flat list they render.
   *
   * Empty array when the task has no declared dependencies OR when the
   * source doesn't track deps (local threads always).
   */
  readonly dependsOn: ReadonlyArray<string>;
  /**
   * Count of outstanding blockers: deps whose status is NOT 'done'.
   * Zero when the task has no deps or all deps are complete.
   */
  readonly blockedByCount: number;
  /**
   * True when the item is actionable RIGHT NOW — nothing upstream is
   * still blocking. For CMMD tasks this means `status === 'todo'` AND
   * every declared dep is done. Local threads are always ready (they're
   * agent-driven and don't participate in the dep graph).
   *
   * Not the opposite of `blockedByCount > 0` — a task with deps all
   * complete but status `done` is also NOT ready (it's finished).
   */
  readonly isReady: boolean;
}

export type HomebaseView = "my-work" | "active-agents" | "queue" | "all";
export type HomebaseGroupBy = "project" | "campaign" | "status" | "none";
export type HomebaseSortBy = "priority" | "activity" | "due-date" | "created";

export interface WorkItemGroup {
  readonly key: string;
  readonly label: string;
  readonly items: readonly WorkItem[];
}

// ---------------------------------------------------------------------------
// Derivation helpers
//
// deriveAgentStatus / deriveAgentProvider / resolveActivityTimestamp and the
// task<->thread anchor matching now live in ~/lib/taskThreadLink (imported
// above) so the CMMD project task surface can share them.
// ---------------------------------------------------------------------------

function resolvePriorityWeight(priority: string | null): number {
  const normalized = priority?.trim().toLowerCase();
  if (!normalized) return 0;
  if (["critical", "urgent"].includes(normalized)) return 5;
  if (["high", "p1"].includes(normalized)) return 4;
  if (["medium", "normal", "p2"].includes(normalized)) return 3;
  if (["low", "minor", "p3"].includes(normalized)) return 2;
  return 1;
}

// ---------------------------------------------------------------------------
// Build unified list from both data sources
// ---------------------------------------------------------------------------

export function buildUnifiedWorkItems(
  taskRows: readonly NoActiveThreadCommandTaskRow[],
  threadSummaries: readonly SidebarThreadSummary[],
  projectNameByRef: ReadonlyMap<string, string>,
): WorkItem[] {
  // Index CMMD tasks by raw id for dep status lookup.
  const taskStatusById = new Map<string, string | null>();
  for (const task of taskRows) {
    taskStatusById.set(task.id, task.status);
  }
  const isDepDone = (rawDepId: string): boolean => {
    const status = taskStatusById.get(rawDepId);
    // Unknown dep id: treat as not-blocking so a missing reference doesn't
    // wedge the task forever. (The CMMD side enforces referential integrity
    // at write time; this is a belt-and-braces guard for partial responses.)
    if (status === undefined) return true;
    return status === "done" || status === "completed" || status === "closed";
  };

  const linkedThreadByTaskId = buildLinkedThreadByTaskId(threadSummaries);

  const fromTasks: WorkItem[] = taskRows.map((task) => {
    const taskDependsOn = Array.isArray(task.dependsOn) ? task.dependsOn : [];
    const dependsOnWorkItemIds = taskDependsOn.map((rawId) => `cmmd:${rawId}`);
    const blockedByCount = taskDependsOn.filter((rawId) => !isDepDone(rawId)).length;
    const isReady = task.status === "todo" && blockedByCount === 0;
    const linkedThread = resolveLinkedTaskThread(linkedThreadByTaskId, task.id);
    const linkedAgentStatus = linkedThread ? deriveAgentStatus(linkedThread) : ("none" as const);
    const linkedAgentProvider = linkedThread ? deriveAgentProvider(linkedThread.session) : null;
    const linkedActivityAt = linkedThread ? resolveActivityTimestamp(linkedThread) : null;
    const taskActivityAt = task.updatedAt ?? new Date().toISOString();
    const linkedNeedsAttention =
      linkedThread?.hasPendingApprovals === true ||
      linkedThread?.hasPendingUserInput === true ||
      linkedThread?.hasActionableProposedPlan === true ||
      linkedAgentStatus === "error";
    const activityAt =
      linkedActivityAt && Date.parse(linkedActivityAt) > Date.parse(taskActivityAt)
        ? linkedActivityAt
        : taskActivityAt;
    return {
      id: `cmmd:${task.id}`,
      title: task.title,
      source: "cmmd-task" as const,
      status: task.status,
      priority: task.priority,
      projectName: task.projectName,
      cmmdProjectId: task.projectId,
      projectIcon: task.projectIcon ?? null,
      projectIconUrl: task.projectIconUrl ?? null,
      campaignName: task.campaignName,
      workspaceId: task.workspaceId,
      workspaceName: task.workspaceName,
      workspaceIconUrl: task.workspaceIconUrl ?? null,
      teamId: task.teamId,
      teamName: task.teamName,
      assigneeId: task.assigneeId,
      dueDate: task.dueDate,
      agentStatus: linkedAgentStatus,
      agentProvider: linkedAgentProvider,
      environmentId: linkedThread?.environmentId ?? null,
      threadId: linkedThread?.id ?? null,
      projectId: linkedThread?.projectId ?? null,
      activityAt,
      hasActionableProposedPlan: linkedThread?.hasActionableProposedPlan ?? false,
      needsAttention: linkedNeedsAttention,
      dependsOn: dependsOnWorkItemIds,
      blockedByCount,
      isReady,
    };
  });

  const fromThreads: WorkItem[] = threadSummaries
    .filter((summary) => !summary.archivedAt)
    .map((summary) => {
      const agentStatus = deriveAgentStatus(summary);
      const needsAttention =
        summary.hasPendingApprovals ||
        summary.hasPendingUserInput ||
        summary.hasActionableProposedPlan ||
        agentStatus === "error";

      const projectKey = `${summary.environmentId}:${summary.projectId}`;

      return {
        id: `thread:${summary.environmentId}:${summary.id}`,
        title: summary.title,
        source: "local-thread" as const,
        status: agentStatus === "running" ? "running" : agentStatus === "idle" ? "idle" : null,
        priority: needsAttention ? "high" : null,
        projectName: projectNameByRef.get(projectKey) ?? "Unknown project",
        cmmdProjectId: null,
        projectIcon: null,
        projectIconUrl: null,
        campaignName: null,
        workspaceId: null,
        workspaceName: null,
        workspaceIconUrl: null,
        teamId: null,
        teamName: null,
        assigneeId: null,
        dueDate: null,
        agentStatus,
        agentProvider: deriveAgentProvider(summary.session),
        environmentId: summary.environmentId,
        threadId: summary.id,
        projectId: summary.projectId,
        activityAt: resolveActivityTimestamp(summary),
        hasActionableProposedPlan: summary.hasActionableProposedPlan,
        needsAttention,
        // Local threads don't participate in the dependency graph today —
        // they're agent-driven and always actionable when not in an error.
        dependsOn: [],
        blockedByCount: 0,
        isReady: agentStatus !== "error",
      };
    });

  return [...fromTasks, ...fromThreads];
}

// ---------------------------------------------------------------------------
// Filter
// ---------------------------------------------------------------------------

export function filterWorkItems(items: readonly WorkItem[], view: HomebaseView): WorkItem[] {
  switch (view) {
    case "my-work":
      return items.filter(
        (item) =>
          item.needsAttention || item.agentStatus === "running" || item.source === "cmmd-task",
      );
    case "active-agents":
      return items.filter(
        (item) =>
          item.agentStatus === "running" ||
          item.agentStatus === "pending-approval" ||
          item.agentStatus === "pending-input" ||
          item.agentStatus === "error",
      );
    case "queue":
      return items.filter((item) => item.source === "cmmd-task");
    case "all":
      return [...items];
  }
}

// ---------------------------------------------------------------------------
// Sort
// ---------------------------------------------------------------------------

export function sortWorkItems(items: WorkItem[], sortBy: HomebaseSortBy): WorkItem[] {
  return items.toSorted((a, b) => {
    switch (sortBy) {
      case "priority": {
        const aWeight = resolvePriorityWeight(a.priority);
        const bWeight = resolvePriorityWeight(b.priority);
        if (aWeight !== bWeight) return bWeight - aWeight;
        return new Date(b.activityAt).getTime() - new Date(a.activityAt).getTime();
      }
      case "activity":
        return new Date(b.activityAt).getTime() - new Date(a.activityAt).getTime();
      case "due-date": {
        const aDate = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
        const bDate = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
        if (aDate !== bDate) return aDate - bDate;
        return new Date(b.activityAt).getTime() - new Date(a.activityAt).getTime();
      }
      case "created":
        return new Date(b.activityAt).getTime() - new Date(a.activityAt).getTime();
    }
  });
}

// ---------------------------------------------------------------------------
// Group
// ---------------------------------------------------------------------------

export function groupWorkItems(
  items: readonly WorkItem[],
  groupBy: HomebaseGroupBy,
): WorkItemGroup[] {
  if (groupBy === "none") {
    return [{ key: "__all", label: "All items", items }];
  }

  const groups = new Map<string, WorkItem[]>();
  const labelByKey = new Map<string, string>();

  for (const item of items) {
    let key: string;
    let label: string;

    switch (groupBy) {
      case "project":
        key = item.projectName;
        label = item.projectName;
        break;
      case "campaign":
        key = item.campaignName ?? "__none";
        label = item.campaignName ?? "No campaign";
        break;
      case "status": {
        const statusKey =
          item.agentStatus !== "none" ? item.agentStatus : (item.status ?? "unknown");
        key = statusKey;
        label = statusKey.replace(/-/g, " ").replace(/^\w/, (c) => c.toUpperCase());
        break;
      }
    }

    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key)!.push(item);
    labelByKey.set(key, label);
  }

  return Array.from(groups.entries()).map(([key, groupItems]) => ({
    key,
    label: labelByKey.get(key) ?? key,
    items: groupItems,
  }));
}
